import { buildImgSrc } from '@/helpers/image-url'
import { getImageOrigin } from '@/scopes/account/common/utils/getImageOrigin'
import { Card } from '@/scopes/common/components/Card'
import { useConfiguration } from '@/scopes/common/infrastructure/config/ConfigProvider'
import {
  mqMax,
  tok,
  tokPx,
} from '@sunwebgroup/ui-components'
import {
  Button,
  LazyImage,
  Typography,
} from '@sunwebgroup/ui-components/uds'
import { useIntl } from 'react-intl'
import { styled } from 'styled-components'
import { getNoUpcomingBookingImageUrl } from '../../domain/policies/noUpcomingBookingImageUrl'
import { getSplashPageUrl } from '../../domain/policies/splashPageUrl'

const IMAGE_WIDTH = 960
const IMAGE_HEIGHT = 450

const Container = styled(Card)`
  position: relative;

  @media ${mqMax('primitives_breakpoint_s_max')} {
    display: flex;
    flex-direction: column;
  }
`

const TextContainer = styled.div`
  padding: ${tokPx('primitives_space_16')} ${tokPx('primitives_space_24')};
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  right: ${tokPx('primitives_area_size_120')};
  background-color: ${tok('semantics_views_background_color')};
  border-radius: ${tokPx('semantics_cards_corner_radius')};
  max-width: ${tokPx('primitives_area_size_360')};

  @media ${mqMax('primitives_breakpoint_s_max')} {
    top: auto;
    position: static;
    transform: none;
    right: auto;
    max-width: none;
  }
`

const Title = styled(Typography)`
  margin-bottom: ${tokPx('primitives_space_8')};

  @media ${mqMax('primitives_breakpoint_s_max')} {
    ${Typography.modVariant('display-g-regular')};
  }
`

const Text = styled(Typography)`
  margin-bottom: ${tokPx('primitives_space_16')};

  @media ${mqMax('primitives_breakpoint_s_max')} {
    ${Typography.modVariant('body-m-regular-snug')};
  }
`

export function NoUpcomingBookingCard({ className }: { className?: string }) {
  const intl = useIntl()
  const siteName = useConfiguration('siteName')
  const noUpcomingBookingImageUrl = getNoUpcomingBookingImageUrl(siteName)
  const splashPageUrl = getSplashPageUrl(siteName)
  const imageSrc = noUpcomingBookingImageUrl.split('?')[0]
  const imageOrigin = getImageOrigin(noUpcomingBookingImageUrl)
  const placeholderSrc = buildImgSrc(
    imageSrc,
    IMAGE_WIDTH / 4,
    IMAGE_HEIGHT / 4,
    imageOrigin,
  )
  const optimizedImageSrc = buildImgSrc(
    imageSrc,
    IMAGE_WIDTH * 2,
    IMAGE_HEIGHT * 2,
    imageOrigin,
  )

  return (
    <Container className={className}>
      <LazyImage
        placeholderSrc={placeholderSrc}
        src={optimizedImageSrc}
        alt=""
        width={IMAGE_WIDTH}
        height={IMAGE_HEIGHT}
      />
      <TextContainer>
        <Title variant="display-e-regular" forwardedAs="h2">
          {intl.formatMessage({ id: 'no_upcoming_bookings_title' })}
        </Title>
        <Text variant="body-l-regular-snug" forwardedAs="p">
          {intl.formatMessage({ id: 'no_upcoming_bookings_text' })}
        </Text>
        <Button variant="primary" href={splashPageUrl} forwardedAs="a">
          {intl.formatMessage({ id: 'no_upcoming_bookings_cta' })}
        </Button>
      </TextContainer>
    </Container>
  )
}
